/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import moment from "moment";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ROUTE_PATH } from "../../constants/route";
import { getTrip } from "../../store/reducers/home/apiThunk";
import { fetchOrderConfirmationDetails, selectBagItems } from "../../store/reducers/bag/apiThunk";
import StyledPaymentSuccess from "./StyledPaymentSuccess";
import { toTwoDecimalPlaces } from "../../utils/commonFuntions";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const bagState = useSelector(selectBagItems);
  const [tripDetails, setTripDetails] = useState([]);
  const [orderDetails, setOrderDetails] = useState(null);

  // URL param is first priority — survives Stripe's full-page redirect
  // Redux orderId is second — works for same-session non-redirect flows
  const orderId = searchParams.get("order_id") || bagState?.orderId;

  useEffect(() => {
    const getTripDetails = () => {
      dispatch(getTrip()).then((result) => {
        setTripDetails(result?.payload?.data)
      })
    }
    getTripDetails()
  }, [])

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (orderId) {
        try {
          const response = await dispatch(fetchOrderConfirmationDetails(orderId));
          if (response?.payload?.data) {
            setOrderDetails(response.payload.data);
            return;
          }
        } catch (error) {
          console.error("Error fetching order confirmation details:", error);
        }
      }
      // Third priority — same-session fallback (not available after Stripe redirect)
      const orderData = sessionStorage.getItem("orderData");
      if (orderData) {
        setOrderDetails(JSON.parse(orderData));
      }
    };

    fetchOrderDetails();
  }, [dispatch, orderId]);


  function formatDateToLongForm(dateStr) {
    return moment(dateStr, 'YYYY-MM-DD').format('D MMMM YYYY');
  }

  // API data has orderDetails.items; sessionStorage data is the array directly
  const bagItems = Array.isArray(orderDetails) ? orderDetails : (orderDetails?.items || []);

  const calculatePercentage = (percentage, total) => (percentage / 100) * total;

  const displayTripDetails = orderDetails?.trip_details || {
    city: tripDetails?.[0]?.city_name,
    hotel: tripDetails?.[0]?.hotel_name,
    arrival_date: tripDetails?.[0]?.arrival_date
  };

  const displayPaymentBreakdown = orderDetails?.payment_breakdown || {};

  // First priority: API order-level payment_component (new format: name/type/value/value_added)
  // Second priority: sessionStorage items[0].payment_component (old format: payment_component_name/component_type/component_value)
  const paymentComponents = (() => {
    const fromApi = orderDetails?.payment_component;
    if (fromApi) return fromApi;
    const fromItems = bagItems?.[0]?.payment_component;
    if (fromItems) return fromItems;
    try {
      const stored = sessionStorage.getItem("orderData");
      if (stored) return JSON.parse(stored)?.[0]?.payment_component;
    } catch (_) {}
    return null;
  })();
  return (
    <StyledPaymentSuccess>
      <Box className='payment-success-wrap'>
        <Box className='payment-success-box'>
          <Box className='payment-success-icon-wrap'>
            <DoneIcon sx={{ fontSize: "33px", color: "white" }} />
          </Box>
        </Box>
        <Box className='payment-success-text-wrap'>
          <Typography className='payment-success-text-header'>
            Order Confirmed!
          </Typography>
          <Typography className='payment-success-text-body'>
            Your order has been successfully placed. You can check this order as
            a trip in your Planned Trip Closet.
          </Typography>
        </Box>
        <Box className='trip-deatails'>
          <Typography className='trip-title'>Trip details</Typography>
          <Box className='info-container-2'>
            <Box className='info-container'>
              <ApartmentIcon className='icon-person-1' sx={{ color: "#A78D78" }} />
              <Typography className='bold-text-1'>{displayTripDetails?.hotel}</Typography>
            </Box>
            <Typography className='location-text'>{displayTripDetails?.city}</Typography>
            <Box>
              <Typography className='arrival-text'>
                <Typography className='arrival-text-1'>Arrival date</Typography>
                <Typography className='arrival-bold-span'>{formatDateToLongForm(displayTripDetails?.arrival_date)}</Typography>
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className='payment-summary-wrap'>
          <Box className='payment-summary'>
            <Typography className='payment-summary-header'>
              Amount Summary
            </Typography>
            <Box className="amount-container">
              <Typography className="amount-text">Reservation fee</Typography>
              <Typography className="amount">{displayPaymentBreakdown?.service_fee != null ? `$${toTwoDecimalPlaces(displayPaymentBreakdown?.service_fee)}` : "--"}</Typography>
            </Box>
            {paymentComponents?.length > 0
              ? paymentComponents.map((element) => {
                  // API format: name/value_added (pre-calculated)
                  // Old sessionStorage format: payment_component_name/component_type/component_value
                  const label = element?.name || element?.payment_component_name;
                  const amount = element?.value_added != null
                    ? element.value_added
                    : element?.component_type === "percentage"
                      ? calculatePercentage(element?.component_value, bagItems[0]?.price_per_unit || bagItems[0]?.price)
                      : element?.component_value;
                  return (
                    <Box className="amount-container" key={label}>
                      <Typography className="amount-text">{label}</Typography>
                      <Typography className="amount">{`$${toTwoDecimalPlaces(amount)}`}</Typography>
                    </Box>
                  );
                })
              : null}
            <Box className="amount-container">
              <Typography className="amount-text">
                Subtotal
                <Tooltip
                  title='Payable amount for kept items after trial.'
                  arrow
                  placement='right'
                >
                  <InfoOutlinedIcon fontSize='small' className='infoIcon' />
                </Tooltip>
              </Typography>
              <Typography className="amount">{displayPaymentBreakdown?.subtotal ? `$${toTwoDecimalPlaces(displayPaymentBreakdown?.subtotal)}` : "--"}</Typography>
            </Box>
            <Divider />
            <Box className="amount-container">
              <Typography className="total-amount-text">Total Amount Paid</Typography>
              <Typography className="total-amount">{displayPaymentBreakdown?.service_fee != null ? `$${toTwoDecimalPlaces(displayPaymentBreakdown?.service_fee)}` : "--"}</Typography>
            </Box>
          </Box>
        </Box>
        <Box className='button-wrap'>
          <Button className='continue-shopping-btn' onClick={() => navigate(ROUTE_PATH.HOME)}>Continue shopping</Button>
          <Link to={`${ROUTE_PATH.CLOSET}?tab=Planned`} className="view-planned-trip-btn">View planned trip</Link>
        </Box>
      </Box>
    </StyledPaymentSuccess>
  );
};

export default PaymentSuccessPage;
